import React, { memo, useMemo } from 'react'
import { CirclesGraph } from '../core/CirclesGraph'
import settings from '../settings'
import { GraphLayoutKind, NodeData, NodeType } from '../types'
import { isRoleNode } from '../helpers/nodeKind'
import EnterGroup from './EnterGroup'
import { useGraphRenderContext } from './GraphRenderContext'
import { useGraphSelectedCircleId } from './hooks/useGraphSelectedCircleId'
import { useVisibleNodes } from './hooks/useVisibleNodes'
import CardElement from './nodes/CardElement'
import CardMemberElement from './nodes/CardMemberElement'
import CircleElement from './nodes/CircleElement'
import MemberElement from './nodes/MemberElement'

interface Props {
  graph: CirclesGraph
}

export default memo(function Nodes({ graph }: Props) {
  const { nodes, levelHiddenIds } = useVisibleNodes(graph)
  const selectedCircleId = useGraphSelectedCircleId(graph)
  const enteringIds = graph.enteringIds
  const repositionedIds = graph.repositionedIds
  const isTree = graph.layoutKind === GraphLayoutKind.Tree

  // "My roles" emphasis: the highlighted ids are filled with the accent, the
  // circles hosting them are ringed with it, and every other node drops to a
  // wireframe (see the .highlighted-role / .highlighted-path / .ghost styles).
  // Activation is driven by the toggle being on (ids provided), not by the set
  // being non-empty: an empty set is a valid "emphasize nothing" input.
  const { highlightedCircleIds } = useGraphRenderContext()
  const highlightedIds = useMemo(
    () => new Set(highlightedCircleIds),
    [highlightedCircleIds]
  )
  const highlightActive = highlightedCircleIds !== undefined

  // Ids of the circles leading down to a highlighted node, root included.
  // Walked over graph.nodes (the full layout) and not over the visible nodes:
  // culling drops the children of a circle that is too small on screen, so a
  // highlighted leaf role is absent from the render exactly when zoomed out,
  // which is when these rings are the only thing telling where it lives.
  const highlightedPathIds = useMemo(() => {
    const ids = new Set<string>()
    if (!highlightActive) return ids
    for (const node of graph.nodes) {
      if (node.data.type !== NodeType.Circle) continue
      if (!highlightedIds.has(node.data.id)) continue
      // A highlighted node holding sub-circles is not a role: filling it would
      // hide what it contains, so it joins the path and gets a ring instead.
      let ancestor = isRoleNode(node) ? node.parent : node
      while (ancestor) {
        if (ancestor.data.type === NodeType.Circle) ids.add(ancestor.data.id)
        ancestor = ancestor.parent
      }
    }
    return ids
  }, [graph.nodes, highlightedIds, highlightActive])

  // Mount leaders avatars only when they can be visible (zoom scale near 1),
  // like members: prevents loading hundreds of images at once on page load
  const showLeaders =
    graph.showAllNodes ||
    graph.zoomTransform.k * settings.culling.memberScaleMargin > 1

  // Split entering nodes (added by the last data update) from the rest, and
  // group them by their visual parent. Each group is animated as a single
  // composited layer by EnterGroup, instead of one GPU layer per node.
  // A tree card is not contained in its parent, so a group wrapper cannot be
  // sized to it: entering nodes animate individually there.
  const flat: NodeData[] = []
  const enterGroups = new Map<string, { parent: NodeData; nodes: NodeData[] }>()
  for (const node of nodes) {
    const enterParent =
      node.data.type === NodeType.Member ? node.parent?.parent : node.parent
    if (!isTree && enteringIds.has(node.data.id) && enterParent) {
      let group = enterGroups.get(enterParent.data.id)
      if (!group) {
        group = { parent: enterParent, nodes: [] }
        enterGroups.set(enterParent.data.id, group)
      }
      group.nodes.push(node)
    } else {
      flat.push(node)
    }
  }

  return (
    <>
      {flat.map((node) => {
        const selected = selectedCircleId === node.data.id
        const levelHidden = levelHiddenIds.has(node.data.id)
        // Hide nodes that are moving during a select-relayout (except the
        // focused circle): they would each promote a GPU layer while
        // transitioning. They reappear at their final position once the
        // animation ends (graph clears repositionedIds).
        // A tree shows far fewer nodes at once and has no enclosing circle to
        // hide them into, so its cards just glide to their new position (only
        // their edges are hidden, see GraphTreeLinks).
        const hidden = !isTree && !selected && repositionedIds.has(node.data.id)
        // Emphasis, one state per circle: filled when it is one of the
        // highlighted roles, ringed when it hosts one, wireframe otherwise.
        // The focused circle keeps its normal look, it stays readable.
        const isCircle = node.data.type === NodeType.Circle
        const highlightedRole =
          highlightActive &&
          isCircle &&
          highlightedIds.has(node.data.id) &&
          isRoleNode(node)
        const highlightedPath =
          highlightActive &&
          isCircle &&
          !highlightedRole &&
          highlightedPathIds.has(node.data.id)
        const ghost =
          highlightActive &&
          isCircle &&
          !highlightedRole &&
          !highlightedPath &&
          !selected
        // A member avatar is a photo: it can't go wireframe, so it fades when
        // it sits outside the highlighted roles.
        const ownerCircleId =
          node.data.type === NodeType.Member
            ? node.parent?.parent?.data.id
            : undefined
        const dimmed =
          highlightActive &&
          node.data.type === NodeType.Member &&
          !(ownerCircleId && highlightedIds.has(ownerCircleId))
        return node.data.type === NodeType.Circle ? (
          isTree ? (
            <CardElement
              key={node.data.id}
              node={node}
              selected={selected}
              hidden={hidden}
              highlightedRole={highlightedRole}
              highlightedPath={highlightedPath}
              ghost={ghost}
            />
          ) : (
            <CircleElement
              key={node.data.id}
              node={node}
              selected={selected}
              levelHidden={levelHidden}
              showLeaders={showLeaders}
              hidden={hidden}
              highlightedRole={highlightedRole}
              highlightedPath={highlightedPath}
              ghost={ghost}
            />
          )
        ) : node.data.type === NodeType.Member ? (
          isTree ? (
            <CardMemberElement key={node.data.id} node={node} hidden={hidden} />
          ) : (
            <MemberElement
              key={node.data.id}
              node={node}
              levelHidden={levelHidden}
              hidden={hidden}
              dimmed={dimmed}
            />
          )
        ) : null
      })}
      {Array.from(enterGroups.values()).map((group) => (
        <EnterGroup
          key={group.parent.data.id}
          parent={group.parent}
          nodes={group.nodes}
          levelHiddenIds={levelHiddenIds}
        />
      ))}
    </>
  )
})
